import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Search,
  ChevronDown,
  Leaf,
  Droplets,
  FlaskConical,
  Globe2,
  PackageCheck,
  Truck,
  ShoppingBag,
  Play,
  CheckCircle2,
  Sprout,
  ShieldCheck,
  X,
} from "lucide-react";

// =====================================================
// IMAGES
// Change filenames if your actual image names differ
// =====================================================

import ProductHero from "../image/products-hero.png";
import LettuceImage from "../image/lettuce_2.png";
import SpinachImage from "../image/spinach.png";
import BasilImage from "../image/basil.png";
import MintImage from "../image/mint.png";
import TomatoImage from "../image/tomato.png";
import CucumberImage from "../image/cucumber2.png";
import StrawberryImage from "../image/strawberry.png";
import CapsicumImage from "../image/capsicum.png";
import CorianderImage from "../image/coriander.png";
import MicrogreensImage from "../image/microgreens.png";
import GrowingProcessImage from "../image/growing-process.png";

// =====================================================
// PRODUCT DATA
// =====================================================

const products = [
  {
    slug: "lettuce",
    name: "Lettuce",
    category: "Leafy Greens",
    description: "Crisp, fresh and nutritious",
    image: LettuceImage,
    features: [
      "Rich in vitamins A & K",
      "Low calories, high fiber",
      "Perfect for salads",
    ],
  },

  {
    slug: "spinach",
    name: "Spinach",
    category: "Leafy Greens",
    description: "Tender leaves, rich in iron",
    image: SpinachImage,
    features: [
      "High in iron & antioxidants",
      "Supports overall health",
      "Available year-round",
    ],
  },

  {
    slug: "basil",
    name: "Basil",
    category: "Herbs",
    description: "Aromatic and flavourful",
    image: BasilImage,
    features: [
      "Rich aroma and taste",
      "Ideal for culinary use",
      "Fresh and chemical-free",
    ],
  },

  {
    slug: "mint",
    name: "Mint",
    category: "Herbs",
    description: "Refreshing and natural",
    image: MintImage,
    features: [
      "Cooling and fresh flavour",
      "Great for beverages",
      "High-quality leaves",
    ],
  },

  {
    slug: "tomato-cherry",
    name: "Tomato (Cherry)",
    category: "Fruits",
    description: "Sweet, juicy and fresh",
    image: TomatoImage,
    features: [
      "Rich in vitamins C & K",
      "Naturally sweet taste",
      "Grown in controlled environment",
    ],
  },

  {
    slug: "cucumber",
    name: "Cucumber",
    category: "Vegetables",
    description: "Crisp and hydrating",
    image: CucumberImage,
    features: [
      "High water content",
      "Fresh and crunchy",
      "Ideal for healthy diet",
    ],
  },

  {
    slug: "strawberry",
    name: "Strawberry",
    category: "Fruits",
    description: "Sweet and delicious",
    image: StrawberryImage,
    features: [
      "Naturally sweet taste",
      "Rich in antioxidants",
      "Premium quality produce",
    ],
  },

  {
    slug: "capsicum",
    name: "Capsicum (Bell Pepper)",
    category: "Vegetables",
    description: "Colourful and nutritious",
    image: CapsicumImage,
    features: [
      "Rich in vitamins A & C",
      "Crisp and flavourful",
      "Available year-round",
    ],
  },

  {
    slug: "coriander",
    name: "Coriander",
    category: "Herbs",
    description: "Fresh and aromatic",
    image: CorianderImage,
    features: [
      "Strong natural aroma",
      "Used in daily cooking",
      "Clean and chemical-free",
    ],
  },

  {
    slug: "microgreens",
    name: "Microgreens",
    category: "Microgreens",
    description: "Small leaves, big nutrition",
    image: MicrogreensImage,
    features: [
      "Nutrient-dense superfood",
      "Perfect for salads & garnishes",
      "Quick growth cycle",
    ],
  },
];
// =====================================================
// CATEGORIES
// =====================================================

const categories = [
  {
    name: "All Products",
    value: "All",
    icon: Leaf,
  },
  {
    name: "Leafy Greens",
    value: "Leafy Greens",
    icon: Sprout,
  },
  {
    name: "Herbs",
    value: "Herbs",
    icon: Leaf,
  },
  {
    name: "Fruits",
    value: "Fruits",
    icon: Sprout,
  },
  {
    name: "Vegetables",
    value: "Vegetables",
    icon: Sprout,
  },
  {
    name: "Microgreens",
    value: "Microgreens",
    icon: Leaf,
  },
  {
    name: "Specialty Crops",
    value: "Specialty Crops",
    icon: Sprout,
  },
];

// =====================================================
// FAQ DATA
// =====================================================

const faqs = [
  {
    question: "Are your products chemical-free?",
    answer:
      "Yes, all our produce is grown using controlled hydroponic systems without harmful chemicals or unnecessary pesticides.",
  },
  {
    question: "Can I place bulk orders?",
    answer:
      "Yes. We supply restaurants, retailers, businesses and institutions. Contact our team for bulk requirements.",
  },
  {
    question: "Do you deliver to other cities?",
    answer:
      "Yes, we can arrange delivery to selected cities depending on crop availability, quantity and location.",
  },
  {
    question: "Can you grow customized crops?",
    answer:
      "Yes. We can explore customized crop production based on your requirements, quantity and growing conditions.",
  },
];

// =====================================================
// COMPONENT
// =====================================================

export default function Products() {
  const [activeCategory, setActiveCategory] = useState("All");
  const [search, setSearch] = useState("");
  const [sortOpen, setSortOpen] = useState(false);
  const [sort, setSort] = useState("Popular");
  const [openFaq, setOpenFaq] = useState(null);

  // ===================================================
  // FILTER PRODUCTS
  // ===================================================

  const filteredProducts = products
    .filter((product) => {
      const categoryMatch =
        activeCategory === "All" ||
        product.category === activeCategory;

      const searchMatch =
        product.name
          .toLowerCase()
          .includes(search.toLowerCase()) ||
        product.category
          .toLowerCase()
          .includes(search.toLowerCase());

      return categoryMatch && searchMatch;
    })
    .sort((a, b) => {
      if (sort === "A-Z") {
        return a.name.localeCompare(b.name);
      }

      if (sort === "Z-A") {
        return b.name.localeCompare(a.name);
      }

      return 0;
    });

  return (
    <div className="min-h-screen bg-white text-[#10231c]">

      {/* =================================================
          HERO
      ================================================= */}
      <section className="relative overflow-hidden">

        <div className="relative min-h-[500px] lg:min-h-[535px]">

          {/* Background */}
          <img
            src={ProductHero}
            alt="Fresh hydroponic produce"
            className="absolute inset-0 h-full w-full object-cover"
          />

          {/* Left Gradient */}
          <div className="absolute inset-0 bg-gradient-to-r from-white via-white/95 to-white/10" />

          {/* Content */}
          <div className="relative z-10 mx-auto flex min-h-[500px] max-w-[1400px] items-center px-5 py-16 lg:min-h-[535px] lg:px-10">

            <div className="max-w-[550px]">

              {/* Breadcrumb */}
              <div className="mb-6 flex items-center gap-2 text-sm font-medium text-gray-600">
                <Link
                  to="/"
                  className="hover:text-[#087f4f]"
                >
                  Home
                </Link>

                <span>›</span>

                <span className="text-[#087f4f]">
                  Products
                </span>
              </div>

              {/* Label */}
              <p className="mb-2 text-sm font-bold uppercase tracking-[0.15em] text-[#16854f]">
                Our Crops & Products
              </p>

              {/* Heading */}
              <h1 className="text-4xl font-extrabold leading-[1.04] sm:text-5xl lg:text-[52px]">
                Fresh Food
                <br />
                for a{" "}
                <span className="text-[#087f4f]">
                  Healthier
                </span>
                <br />
                Tomorrow
              </h1>

              {/* Description */}
              <p className="mt-5 max-w-[500px] text-base leading-6 text-gray-600">
                Locally grown, nutrient-rich and chemical-free
                produce using advanced hydroponic technology.
                Fresh, safe and sustainable food for homes,
                businesses and communities.
              </p>

              {/* Hero Features */}
              <div className="mt-5 flex flex-wrap gap-x-6 gap-y-3">

                <HeroFeature
                  icon={<Leaf size={24} />}
                  text="100% Fresh & Safe"
                />

                <HeroFeature
                  icon={<ShieldCheck size={24} />}
                  text="No Harmful Chemicals"
                />

                <HeroFeature
                  icon={<Droplets size={24} />}
                  text="Rich in Nutrients"
                />

                <HeroFeature
                  icon={<Globe2 size={24} />}
                  text="Sustainable Farming"
                />

              </div>

              {/* Buttons */}
              <div className="mt-7 flex flex-wrap gap-3">

                <a
                  href="#products"
                  className="inline-flex items-center gap-2 rounded-full bg-[#087f4f] px-6 py-3 text-sm font-bold text-white transition hover:bg-[#066b42]"
                >
                  Explore Our Products
                  <ArrowRight size={16} />
                </a>

                <button
                  type="button"
                  className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-6 py-3 text-sm font-bold text-gray-700 shadow-sm"
                >
                  <span className="flex h-6 w-6 items-center justify-center rounded-full bg-[#e8f7ee] text-[#087f4f]">
                    <span className="text-[10px]">
                      ↓
                    </span>
                  </span>

                  Download Product Brochure
                </button>

              </div>

            </div>


            {/* Right Green Panel */}
            <div className="absolute bottom-0 right-0 hidden h-[240px] w-[290px] overflow-hidden rounded-tl-[130px] bg-[#005536] lg:block">

              <div className="flex h-full flex-col justify-center px-10">

                <p
                  className="text-center text-2xl font-semibold italic leading-tight text-white"
                  style={{
                    fontFamily: "Georgia, serif",
                  }}
                >
                  Good Food
                  <br />
                  Brighter Tomorrow
                </p>

                <div className="mx-auto mt-3 h-[3px] w-36 rotate-[-5deg] rounded-full bg-[#75d337]" />

                <div className="mt-5 space-y-2 text-sm font-medium text-white">

                  <div className="flex items-center gap-3">
                    <FlaskConical size={18} />
                    <span>Pesticide Free</span>
                  </div>

                  <div className="flex items-center gap-3">
                    <Sprout size={18} />
                    <span>Hydroponically Grown</span>
                  </div>

                  <div className="flex items-center gap-3">
                    <Leaf size={18} />
                    <span>Farm Fresh</span>
                  </div>

                  <div className="flex items-center gap-3">
                    <ShieldCheck size={18} />
                    <span>Better Nutrition</span>
                  </div>

                </div>

              </div>

            </div>

          </div>

        </div>
      </section>


      {/* =================================================
          CATEGORY FILTER
      ================================================= */}
      <section className="border-b border-green-100 bg-[#f7fbf8]">

        <div className="mx-auto flex max-w-[1400px] gap-2 overflow-x-auto px-5 py-3 scrollbar-hide lg:px-10">

          {categories.map((category) => {
            const Icon = category.icon;
            const active =
              activeCategory === category.value;

            return (
              <button
                key={category.value}
                onClick={() =>
                  setActiveCategory(category.value)
                }
                className={`flex flex-shrink-0 items-center gap-2 rounded-lg border px-5 py-2.5 text-sm font-semibold transition ${
                  active
                    ? "border-[#087f4f] bg-[#087f4f] text-white"
                    : "border-green-100 bg-white text-gray-600 hover:border-[#087f4f] hover:text-[#087f4f]"
                }`}
              >
                <Icon size={18} />
                {category.name}
              </button>
            );
          })}

        </div>

      </section>


      {/* =================================================
          PRODUCTS
      ================================================= */}
      <section
        id="products"
        className="bg-white py-10 lg:py-12"
      >
        <div className="mx-auto max-w-[1400px] px-5 lg:px-10">

          {/* Header */}
          <div className="flex flex-col gap-5 md:flex-row md:items-end md:justify-between">

            <div>

              <h2 className="text-3xl font-extrabold">
                Our Fresh Produce
              </h2>

              <p className="mt-2 text-sm text-gray-600">
                Discover a variety of fresh, nutritious and premium
                quality crops grown in our controlled hydroponic farms.
              </p>

            </div>


            {/* Search + Sort */}
            <div className="flex flex-col gap-2 sm:flex-row">

              {/* Search */}
              <div className="relative">

                <Search
                  size={17}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                />

                <input
                  type="text"
                  placeholder="Search products..."
                  value={search}
                  onChange={(e) =>
                    setSearch(e.target.value)
                  }
                  className="h-11 w-full rounded-lg border border-gray-200 pl-10 pr-4 text-sm outline-none transition focus:border-[#087f4f] sm:w-[210px]"
                />

              </div>


              {/* Sort */}
              <div className="relative">

                <button
                  onClick={() =>
                    setSortOpen(!sortOpen)
                  }
                  className="flex h-11 w-full items-center justify-between gap-6 rounded-lg border border-gray-200 bg-white px-4 text-sm font-medium text-gray-700 "
                >
                  Sort by: {sort}
                  <ChevronDown size={16} />
                </button>

                {sortOpen && (
                  <div className="absolute right-0 top-12 z-30 w-full overflow-hidden rounded-lg border border-gray-100 bg-white shadow-lg sm:w-[145px]">

                    {["Popular", "A-Z", "Z-A"].map(
                      (option) => (
                        <button
                          key={option}
                          onClick={() => {
                            setSort(option);
                            setSortOpen(false);
                          }}
                          className="block w-full px-4 py-2.5 text-left text-sm hover:bg-green-50"
                        >
                          {option}
                        </button>
                      )
                    )}

                  </div>
                )}

              </div>

            </div>

          </div>


          {/* Product Grid */}
          <div className="mt-7 grid gap-4 sm:grid-cols-2 lg:grid-cols-5">

            {filteredProducts.map((product) => (
              <ProductCard
                key={product.name}
                product={product}
              />
            ))}

          </div>


          {/* Empty */}
          {filteredProducts.length === 0 && (
            <div className="py-16 text-center">

              <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-green-50 text-[#087f4f]">
                <Search size={28} />
              </div>

              <h3 className="mt-4 text-xl font-bold">
                No products found
              </h3>

              <p className="mt-2 text-sm text-gray-500">
                Try another product or category.
              </p>

              <button
                onClick={() => {
                  setSearch("");
                  setActiveCategory("All");
                }}
                className="mt-4 rounded-full bg-[#087f4f] px-5 py-2 text-sm font-bold text-white"
              >
                Clear Filters
              </button>

            </div>
          )}

        </div>
      </section>


      {/* =================================================
          GROWING PROCESS
      ================================================= */}
      <section className="bg-[#f5fbf7] py-10 lg:py-12">

        <div className="mx-auto max-w-[1400px] px-5 lg:px-10">

          <div className="grid items-center gap-8 lg:grid-cols-[1fr_1.4fr_0.9fr]">

            {/* Image */}
            <div className="relative overflow-hidden rounded-xl">

              <img
                src={GrowingProcessImage}
                alt="Hydroponic growing process"
                className="h-[190px] w-full object-cover lg:h-[190px]"
              />

              <button
                className="absolute left-1/2 top-1/2 flex h-14 w-14 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-white text-[#087f4f] shadow-xl"
              >
                <Play
                  size={20}
                  fill="currentColor"
                />
              </button>

            </div>


            {/* Content */}
            <div>

              <p className="text-xs font-bold uppercase tracking-[0.15em] text-[#16854f]">
                From Seed to Harvest
              </p>

              <h2 className="mt-1 text-2xl font-extrabold sm:text-3xl">
                The Polybionic Growing Process
              </h2>

              <p className="mt-3 text-sm leading-6 text-gray-600">
                Our hydroponic crops are grown in a clean,
                controlled environment using advanced technology.
                This ensures consistent quality, higher yields and
                fresh produce throughout the year.
              </p>

              <button className="mt-5 inline-flex items-center gap-2 rounded-full bg-[#087f4f] px-5 py-2.5 text-sm font-bold text-white">
                Watch Our Farm Tour
                <ArrowRight size={16} />
              </button>

            </div>


            {/* Stats */}
            <div className="rounded-xl bg-[#007444] p-6 text-white">

              <h3 className="text-lg font-bold">
                Fresh | Local | Sustainable
              </h3>

              <div className="mt-5 grid grid-cols-2 gap-5">

                <Stat
                  icon={<Leaf size={21} />}
                  number="365"
                  text="Days Fresh Produce"
                />

                <Stat
                  icon={<Droplets size={21} />}
                  number="90%"
                  text="Less Water Usage"
                />

                <Stat
                  icon={<ShieldCheck size={21} />}
                  number="0%"
                  text="Harmful Chemicals"
                />

                <Stat
                  icon={<CheckCircle2 size={21} />}
                  number="100%"
                  text="Quality Controlled"
                />

              </div>

            </div>

          </div>

        </div>

      </section>


      {/* =================================================
          CULTIVATION + PACKAGING
      ================================================= */}
      <section className="bg-white py-10 lg:py-12">

        <div className="mx-auto grid max-w-[1400px] gap-10 px-5 lg:grid-cols-2 lg:px-10">

          {/* CULTIVATION */}
          <div>

            <h2 className="text-xl font-extrabold">
              Cultivation Details
            </h2>

            <p className="mt-1 text-sm text-gray-600">
              We use modern hydroponic systems to grow crops
              with precision and care.
            </p>

            <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-4">

              <InfoCard
                icon={<FlaskConical size={22} />}
                title="Controlled Environment"
                text="Ideal temperature, humidity and light"
              />

              <InfoCard
                icon={<Droplets size={22} />}
                title="Nutrient-Rich Water"
                text="Balanced nutrition for healthy growth"
              />

              <InfoCard
                icon={<Leaf size={22} />}
                title="Soilless Farming"
                text="Clean, hygienic and efficient"
              />

              <InfoCard
                icon={<Globe2 size={22} />}
                title="Year-Round Production"
                text="Fresh produce in all seasons"
              />

            </div>

          </div>


          {/* PACKAGING */}
          <div>

            <h2 className="text-xl font-extrabold">
              Packaging & Supply
            </h2>

            <p className="mt-1 text-sm text-gray-600">
              Freshness from our farm to your table.
            </p>

            <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-4">

              <InfoCard
                icon={<PackageCheck size={22} />}
                title="Hygienic Packaging"
                text="Keeps produce fresh and safe"
              />

              <InfoCard
                icon={<Truck size={22} />}
                title="Bulk Supply"
                text="For businesses, retailers and restaurants"
              />

              <InfoCard
                icon={<ShoppingBag size={22} />}
                title="Custom Orders"
                text="As per your requirement"
              />

              <InfoCard
                icon={<Truck size={22} />}
                title="Timely Delivery"
                text="Across cities with care"
              />

            </div>

          </div>

        </div>

      </section>


      {/* =================================================
          CUSTOM CROP CTA
      ================================================= */}
      <section className="overflow-hidden">

        <div className="grid lg:grid-cols-[1.4fr_1fr]">

          {/* Green CTA */}
          <div className="bg-[#005536] px-5 py-8 lg:px-10">

            <div className="mx-auto flex max-w-[650px] items-center gap-5 lg:ml-auto">

              <div className="hidden h-14 w-14 flex-shrink-0 items-center justify-center rounded-full bg-white/10 text-[#7bda58] sm:flex">
                <Leaf size={30} />
              </div>

              <div>

                <h2 className="text-2xl font-extrabold text-white">
                  Looking for a Specific Crop?
                </h2>

                <p className="mt-1 text-sm leading-6 text-white/80">
                  We also grow customized crops based on your needs.
                  Contact us to discuss your requirements.
                </p>

              </div>

              <Link
                to="/contact"
                className="ml-auto hidden flex-shrink-0 items-center gap-2 rounded-full bg-white px-5 py-2.5 text-xs font-bold text-[#087f4f] sm:inline-flex"
              >
                Get a Custom Quote
                <ArrowRight size={15} />
              </Link>

            </div>

          </div>


          {/* Brochure */}
          <div className="bg-[#edf9f1] px-5 py-8 lg:px-10">

            <div className="flex items-center justify-between gap-5">

              <div>

                <h3 className="text-lg font-extrabold">
                  Download Our Product List
                </h3>

                <p className="mt-1 text-xs leading-5 text-gray-600">
                  Get detailed information about all our crops,
                  availability and pricing.
                </p>

                <button className="mt-3 inline-flex items-center gap-2 rounded-full border border-[#087f4f] px-4 py-2 text-xs font-bold text-[#087f4f]">
                  <span>▣</span>
                  Download Brochure
                </button>

              </div>

              <div className="hidden h-24 w-24 overflow-hidden rounded-full sm:block">
                <img
                  src={LettuceImage}
                  alt="Fresh lettuce"
                  className="h-full w-full object-cover"
                />
              </div>

            </div>

          </div>

        </div>

      </section>


      {/* =================================================
          FAQ
      ================================================= */}
      <section className="bg-white py-10 lg:py-12">

        <div className="mx-auto max-w-[1400px] px-5 lg:px-10">

          <h2 className="text-2xl font-extrabold sm:text-3xl">
            Frequently Asked Questions
          </h2>


          <div className="mt-5 grid gap-3 md:grid-cols-2">

            {faqs.map((faq, index) => {
              const isOpen = openFaq === index;

              return (
                <div
                  key={index}
                  className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm"
                >

                  <button
                    onClick={() =>
                      setOpenFaq(
                        isOpen ? null : index
                      )
                    }
                    className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left"
                  >

                    <span className="flex items-center gap-3 text-sm font-bold">

                      <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-[#e8f7ee] text-[#087f4f]">
                        ?
                      </span>

                      {faq.question}

                    </span>

                    {isOpen ? (
                      <X
                        size={18}
                        className="flex-shrink-0 text-[#087f4f]"
                      />
                    ) : (
                      <ChevronDown
                        size={18}
                        className="flex-shrink-0 text-gray-500"
                      />
                    )}

                  </button>


                  {isOpen && (
                    <div className="border-t border-gray-100 px-5 py-4 text-sm leading-6 text-gray-600">
                      {faq.answer}
                    </div>
                  )}

                </div>
              );
            })}

          </div>

        </div>

      </section>

    </div>
  );
}


// =====================================================
// PRODUCT CARD
// =====================================================

function ProductCard({ product }) {
  return (
    <div className="group overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm transition duration-300 hover:-translate-y-1 hover:shadow-lg">

      {/* Image */}
      <div className=" overflow-hidden bg-gray-50">

        <img
          src={product.image}
          alt={product.name}
          className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
        />

      </div>


      {/* Content */}
      <div className="p-4">

        <h3 className="text-base font-bold">
          {product.name}
        </h3>

        <p className="mt-1 text-xs text-gray-500">
          {product.description}
        </p>


        <div className="mt-3 space-y-1.5">

          {product.features.map((feature, index) => (
            <div
              key={index}
              className="flex items-start gap-1.5 text-[11px] leading-4 text-gray-600"
            >
              <CheckCircle2
                size={13}
                className="mt-[1px] flex-shrink-0 text-[#159653]"
              />

              <span>
                {feature}
              </span>

            </div>
          ))}

        </div>


<Link
  to={`/products/${product.slug}`}
  className="mt-4 inline-flex items-center gap-1 rounded-full border border-green-300 px-3 py-1.5 text-xs font-bold text-[#087f4f] transition hover:bg-[#087f4f] hover:text-white"
>
  View Details
  <ArrowRight size={13} />
</Link>

      </div>

    </div>
  );
}


// =====================================================
// HERO FEATURE
// =====================================================

function HeroFeature({ icon, text }) {
  return (
    <div className="flex items-center gap-2">

      <span className="text-[#087f4f]">
        {icon}
      </span>

      <span className="text-xs font-bold text-gray-700">
        {text}
      </span>

    </div>
  );
}


// =====================================================
// STAT
// =====================================================

function Stat({ icon, number, text }) {
  return (
    <div>

      <div className="flex items-center gap-2">
        {icon}

        <span className="text-xl font-extrabold">
          {number}
        </span>
      </div>

      <p className="mt-1 text-[10px] leading-4 text-white/75">
        {text}
      </p>

    </div>
  );
}


// =====================================================
// INFO CARD
// =====================================================

function InfoCard({ icon, title, text }) {
  return (
    <div className="rounded-xl border border-gray-100 bg-[#f8fbf9] p-4 text-center">

      <div className="mx-auto flex h-10 w-10 items-center justify-center rounded-full bg-green-100 text-[#087f4f]">
        {icon}
      </div>

      <h3 className="mt-3 text-xs font-bold leading-4">
        {title}
      </h3>

      <p className="mt-1 text-[10px] leading-4 text-gray-500">
        {text}
      </p>

    </div>
  );
}